import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { DetailsModalContext } from '../../../context';
import {
  StoryCard,
  StoryCardContentArticlePost,
  StoryCardContentArticleQuote,
  StoryCardContentGallery,
  StoryCardContentHeadline,
  StoryCardContentSourceName,
  StoryCardContentTitle,
  StoryCardTeaserImageStyle,
  StoryCardType,
} from '../../../services';
import {
  CardOpenButton,
  EventItemUnavailable,
  GalleryItem,
  IconLabel,
  getCssVar,
  getLocale,
  useCardDate,
  useIsEventUnavailable,
} from '../../../shared';

import classes from './Teaser.module.scss';

interface TeaserProps {
  card: StoryCard;
}

export const Teaser: FunctionComponent<TeaserProps> = memo(({ card }) => {
  const navigate = useNavigate();

  const { setDetailsModalData } = useContext(DetailsModalContext);

  const isEventUnavailable = useIsEventUnavailable();

  const { storyId, id: cardId, type, content, dateToDisplay, event } = card;

  const date = useCardDate(dateToDisplay);

  const { title } = content as StoryCardContentTitle;
  const { sourceName } = content as StoryCardContentSourceName;
  const { headline } = content as StoryCardContentHeadline;
  const { gallery } = content as StoryCardContentGallery;
  const { styles } = content as StoryCardContentArticlePost;
  const { url } = content as StoryCardContentArticleQuote;

  const bigTeaser = useMemo(
    () => styles?.teaserImageStyle === StoryCardTeaserImageStyle.BIG_WITHOUT_TEXT,
    [styles?.teaserImageStyle]
  );

  const teaserClickHandler = useCallback(() => {
    if (url && [StoryCardType.ARTICLE, StoryCardType.QUOTE].includes(type)) {
      window.open(url, '_blank', 'noreferrer');
      return;
    }

    isMobileOnly
      ? navigate(`/details/${storyId}/${cardId}`)
      : setDetailsModalData({ storyId, cardId });
  }, [cardId, navigate, setDetailsModalData, storyId, type, url]);

  const headlineText = useMemo(() => {
    if ([StoryCardType.ARTICLE, StoryCardType.POST, StoryCardType.THREAD].includes(type)) {
      return title;
    }

    if (type === StoryCardType.EVENT) {
      return event?.name;
    }

    return headline;
  }, [event?.name, headline, title, type]);

  const previewSrc = useMemo(() => {
    const galleryItem = gallery?.[0] as GalleryItem;
    const imageUrl = galleryItem?.image?.url;

    switch (type) {
      case StoryCardType.IMAGE:
      case StoryCardType.QUOTE:
      case StoryCardType.POST:
      case StoryCardType.ARTICLE:
        return imageUrl;
      case StoryCardType.AUDIO:
        return imageUrl || galleryItem?.audio?.thumb;
      case StoryCardType.VIDEO:
        return imageUrl || galleryItem?.video?.thumb;
      case StoryCardType.PDF:
        return imageUrl || galleryItem?.pdf?.thumb;
      case StoryCardType.EVENT:
        return event?.image?.url;
      default:
        return undefined;
    }
  }, [event?.image?.url, gallery, type]);

  const teaserContent = useMemo(() => {
    if (type === StoryCardType.EVENT && isEventUnavailable(event)) {
      return <EventItemUnavailable />;
    }

    if (bigTeaser) {
      return (
        <div className={classes['teaser__big']}>
          {previewSrc && (
            <div
              className={classes['teaser__big-preview']}
              style={{ backgroundImage: `url(${previewSrc})` }}
            ></div>
          )}
          <div className={classes['teaser__big-content']}>
            {sourceName && (
              <div className={classes['teaser__big-content-source']}>{sourceName}</div>
            )}
            {headlineText && (
              <div className={classes['teaser__big-content-headline']}>
                {headlineText}
                <span className={classes['teaser__big-content-headline-icon']}>
                  <IconLabel
                    iconId={
                      type === StoryCardType.ARTICLE
                        ? 'arrow-rounded-right-up'
                        : 'arrow-rounded-right'
                    }
                    iconSize={16}
                    color={getCssVar('--card-teaser-icon-color')}
                    singleColor
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <>
        {sourceName && (
          <div lang={getLocale()} className={classes['teaser__source']}>
            {sourceName}
          </div>
        )}
        {previewSrc && <img className={classes['teaser__preview']} src={previewSrc} alt={''} />}
        {headlineText && (
          <div className={classes['teaser__headline']}>
            <IconLabel
              iconId={'arrow-rounded-right'}
              iconSize={10}
              label={headlineText}
              color={getCssVar('--special-teaser-title-color')}
              singleColor
              labelFirst
            />
          </div>
        )}
        <div className={classes['teaser__posted-time']}>{date}</div>
      </>
    );
  }, [bigTeaser, date, event, headlineText, isEventUnavailable, previewSrc, sourceName, type]);

  return (
    <div className={classes['teaser']} onClick={teaserClickHandler}>
      <CardOpenButton label={headlineText} />

      {teaserContent}
    </div>
  );
});
